
import React from "react";
import { motion } from "framer-motion";

import {
  FaChild,
  FaBookOpen,
  FaPalette,
  FaHeart,
  FaBrain,
  FaUsers,
  FaSeedling,
  FaStar,
  FaCheckCircle,
  FaLightbulb,
  FaComments,
  FaPuzzlePiece,
  FaAppleAlt,
  FaShapes,
} from "react-icons/fa";

import playImg from "../assets/kinder1.jpeg";
import learningImg from "../assets/kinder2.jpeg";
import creativeImg from "../assets/kinder3.jpeg";


/* =========================================================
   STRATEGIES
========================================================= */

const strategies = [
  {
    title: "Play-Based Learning",
    icon: <FaChild />,
    text: "Children learn naturally through joyful play, exploration and hands-on experiences.",
  },
  {
    title: "Foundational Literacy & Numeracy",
    icon: <FaBookOpen />,
    text: "Strong early skills in language, reading, writing, numbers and logical thinking.",
  },
  {
    title: "Creative Expression",
    icon: <FaPalette />,
    text: "Art, music, movement and imagination encourage children to express themselves.",
  },
  {
    title: "Safe & Nurturing Environment",
    icon: <FaHeart />,
    text: "A caring environment where every child feels secure, valued and encouraged.",
  },
  {
    title: "Holistic Development",
    icon: <FaBrain />,
    text: "We nurture cognitive, physical, creative and emotional development together.",
  },
  {
    title: "Social & Emotional Learning",
    icon: <FaUsers />,
    text: "Children develop empathy, communication, confidence and positive relationships.",
  },
];


/* =========================================================
   JOURNEY
========================================================= */

const journey = [
  {
    number: "01",
    title: "Bal Vatika",
    subtitle: "Nursery",
    text: "Building curiosity, confidence and a love for learning through play.",
  },
  {
    number: "02",
    title: "KG I",
    subtitle: "Early Exploration",
    text: "Developing communication, creativity, early literacy and numeracy skills.",
  },
  {
    number: "03",
    title: "KG II",
    subtitle: "Growing Skills",
    text: "Strengthening foundational concepts through activities and experiences.",
  },
  {
    number: "04",
    title: "Grades I & II",
    subtitle: "Strong Foundations",
    text: "Preparing children for deeper learning with confidence and independence.",
  },
];


/* =========================================================
   MAIN
========================================================= */

const FoundationalStage = () => {
  return (
    <section className="bg-[#fffaf5] overflow-hidden">


      {/* =====================================================
          HERO
      ===================================================== */}

      <div className="relative py-20 md:py-28">

        {/* Decorative background */}

        <div className="absolute top-10 right-0 w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-70" />

        <div className="absolute bottom-0 left-0 w-72 h-72 bg-yellow-100 rounded-full blur-3xl opacity-70" />


        <div className="max-w-[1500px] mx-auto px-6 relative z-10">

          <div className="grid lg:grid-cols-2 gap-16 items-center">


            {/* IMAGE */}

            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >

              {/* Decorative frame */}

              <div className="absolute -top-7 -left-7 w-32 h-32 rounded-full border-[18px] border-orange-100" />

              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-yellow-100" />

              <div className="absolute top-1/2 -left-10 w-5 h-5 bg-orange-400 rounded-full" />

              <div className="absolute top-20 -right-5 w-3 h-3 bg-yellow-400 rounded-full" />


              {/* Image */}

              <div className="relative rounded-[50px] overflow-hidden shadow-[0_35px_100px_rgba(0,0,0,0.15)]">

                <img
                  src={playImg}
                  alt="Foundational Stage"
                  className="w-full h-[580px] md:h-[680px] object-cover hover:scale-105 transition duration-700"
                />

              </div>


              {/* Floating badge WITHOUT covering image */}

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                }}
                className="absolute -bottom-8 left-8 bg-white rounded-[28px] shadow-2xl px-6 py-5 flex items-center gap-4"
              >

                <div className="w-14 h-14 rounded-2xl bg-orange-400 text-white flex items-center justify-center text-2xl">
                  <FaSeedling />
                </div>

                <div>

                  <p className="text-xs uppercase tracking-[3px] text-orange-500 font-black">
                    Early Years
                  </p>

                  <p className="font-black text-[#07294d] text-xl">
                    Growing With Joy
                  </p>

                </div>

              </motion.div>

            </motion.div>



            {/* CONTENT */}

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >

              <div className="inline-flex items-center gap-3 px-5 py-3 bg-white rounded-full shadow-md border border-orange-100 mb-8">

                <div className="w-10 h-10 rounded-full bg-orange-400 text-white flex items-center justify-center">
                  <FaChild />
                </div>

                <span className="uppercase tracking-[4px] text-[#07294d] font-bold text-xs">
                  Foundational Stage
                </span>

              </div>


              <h1 className="text-5xl sm:text-6xl md:text-7xl xl:text-8xl font-black text-[#07294d] leading-[0.92]">

                Building

                <br />

                Strong

                <br />

                <span className="text-orange-400">
                  Foundations
                </span>

              </h1>


              <div className="w-32 h-[7px] bg-orange-400 rounded-full my-9" />


              <p className="text-xl text-gray-700 leading-9 font-bold mb-5">
                Bal Vatika to Class II
              </p>


              <div className="flex flex-wrap gap-2 mb-7">

                {["Nursery", "KG I", "KG II", "Grades I & II"].map(
                  (item, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-orange-50 border border-orange-100 text-orange-600 rounded-full text-sm font-bold"
                    >
                      {item}
                    </span>
                  )
                )}

              </div>


              <p className="text-lg text-gray-600 leading-9 max-w-xl">
                Through play-based, activity-based and experiential learning,
                we build strong foundations in literacy, numeracy,
                communication, creativity and social-emotional development.
              </p>


              {/* Mini feature row */}

              <div className="grid grid-cols-3 gap-3 mt-10">

                <div className="bg-white rounded-2xl p-4 shadow-md border border-orange-50">
                  <FaBookOpen className="text-orange-400 text-xl mb-2" />
                  <p className="text-sm font-bold text-[#07294d]">
                    Literacy
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-md border border-orange-50">
                  <FaPalette className="text-orange-400 text-xl mb-2" />
                  <p className="text-sm font-bold text-[#07294d]">
                    Creativity
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-md border border-orange-50">
                  <FaHeart className="text-orange-400 text-xl mb-2" />
                  <p className="text-sm font-bold text-[#07294d]">
                    Wellbeing
                  </p>
                </div>

              </div>

            </motion.div>

          </div>

        </div>

      </div>



      {/* =====================================================
          CREATIVE INFO STRIP
      ===================================================== */}

      <div className="py-20">

        <div className="max-w-[1400px] mx-auto px-6">

          <div className="grid md:grid-cols-3 gap-6">


            <motion.div
              whileHover={{ y: -8 }}
              className="bg-orange-400 rounded-[35px] p-8 text-white relative overflow-hidden"
            >

              <div className="absolute -right-10 -top-10 w-36 h-36 border-[25px] border-white/10 rounded-full" />

              <FaLightbulb className="text-4xl mb-6" />

              <h3 className="text-2xl font-black mb-3">
                Curiosity First
              </h3>

              <p className="text-white/85 leading-7">
                Every question becomes an opportunity to discover something new.
              </p>

            </motion.div>


            <motion.div
              whileHover={{ y: -8 }}
              className="bg-[#07294d] rounded-[35px] p-8 text-white relative overflow-hidden"
            >

              <div className="absolute -right-12 -bottom-12 w-44 h-44 border-[30px] border-orange-400/10 rounded-full" />

              <FaPuzzlePiece className="text-4xl mb-6 text-orange-400" />

              <h3 className="text-2xl font-black mb-3">
                Learn By Doing
              </h3>

              <p className="text-white/65 leading-7">
                Activities and experiences turn everyday moments into learning.
              </p>

            </motion.div>


            <motion.div
              whileHover={{ y: -8 }}
              className="bg-white rounded-[35px] p-8 border border-orange-100 shadow-xl relative overflow-hidden"
            >

              <div className="absolute right-0 top-0 w-24 h-24 bg-yellow-100 rounded-bl-[80px]" />

              <FaStar className="text-4xl text-orange-400 mb-6" />

              <h3 className="text-2xl font-black text-[#07294d] mb-3">
                Joyful Growth
              </h3>

              <p className="text-gray-600 leading-7">
                Confidence grows when children feel happy, safe and valued.
              </p>

            </motion.div>

          </div>

        </div>

      </div>



      {/* =====================================================
          PHOTO COLLAGE — NO TEXT ON IMAGES
      ===================================================== */}

      <div className="py-24 bg-white">

        <div className="max-w-[1500px] mx-auto px-6">


          <div className="grid lg:grid-cols-12 gap-7 items-center">


            {/* LARGE IMAGE */}

            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >

              <div className="rounded-[45px] overflow-hidden shadow-2xl">

                <img
                  src={learningImg}
                  alt="Children learning"
                  className="w-full h-[620px] object-cover hover:scale-105 transition duration-700"
                />

              </div>

            </motion.div>


            {/* CREATIVE CENTER */}

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="lg:col-span-2 flex flex-col items-center justify-center gap-6"
            >

              <div className="hidden lg:block w-[2px] h-20 bg-orange-200" />

              <div className="w-24 h-24 rounded-full bg-orange-100 flex items-center justify-center text-orange-400 text-4xl shadow-lg">
                <FaSeedling />
              </div>

              <div className="text-center">

                <p className="text-4xl font-black text-[#07294d]">
                  360°
                </p>

                <p className="text-sm font-bold text-gray-500">
                  Holistic Growth
                </p>

              </div>

              <div className="grid grid-cols-2 gap-3">

                <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center text-yellow-600 text-xl">
                  <FaAppleAlt />
                </div>

                <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center text-orange-500 text-xl">
                  <FaShapes />
                </div>

              </div>

              <div className="hidden lg:block w-[2px] h-20 bg-orange-200" />

            </motion.div>


            {/* TWO IMAGES */}

            <div className="lg:col-span-5 grid gap-7">

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="rounded-[40px] overflow-hidden shadow-xl"
              >

                <img
                  src={creativeImg}
                  alt="Creative learning"
                  className="w-full h-[290px] object-cover hover:scale-105 transition duration-700"
                />

              </motion.div>


              {/* Creative blank-space card */}

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-[#fffaf5] rounded-[40px] p-8 border border-orange-100 relative overflow-hidden"
              >

                <div className="absolute right-[-30px] top-[-30px] w-32 h-32 bg-orange-100 rounded-full" />

                <div className="relative z-10 flex items-center gap-6">

                  <div className="w-20 h-20 rounded-[25px] bg-orange-400 text-white flex items-center justify-center text-3xl shadow-lg">
                    <FaPalette />
                  </div>

                  <div>

                    <p className="text-xs uppercase tracking-[3px] text-orange-500 font-black mb-2">
                      Every Child
                    </p>

                    <h3 className="text-2xl font-black text-[#07294d]">
                      Learns Differently
                    </h3>

                    <p className="text-gray-500 mt-2">
                      We make space for every little mind to grow.
                    </p>

                  </div>

                </div>

              </motion.div>

            </div>

          </div>

        </div>

      </div>



      {/* =====================================================
          PHILOSOPHY
      ===================================================== */}

      <div className="relative py-28 bg-[#07294d] overflow-hidden">


        <div className="absolute top-0 right-0 w-[450px] h-[450px] rounded-full border-[70px] border-orange-400/5" />

        <div className="absolute bottom-[-200px] left-[-100px] w-[500px] h-[500px] rounded-full border-[80px] border-white/5" />


        <div className="max-w-[1500px] mx-auto px-6 relative z-10">

          <div className="grid lg:grid-cols-2 gap-16 items-center">


            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >

              <p className="uppercase tracking-[5px] text-orange-400 font-black text-sm mb-6">
                Our Philosophy
              </p>

              <h2 className="text-4xl md:text-6xl font-black text-white leading-tight">

                Little Minds.

                <br />

                <span className="text-orange-400">
                  Big Possibilities.
                </span>

              </h2>

              <div className="w-28 h-[6px] bg-orange-400 rounded-full my-8" />

              <p className="text-white/70 text-lg leading-9 max-w-xl">
                The foundational years shape the way children see
                themselves, others and the world around them.
                We create meaningful experiences that allow every
                child to discover their strengths and develop a
                lifelong love for learning.
              </p>


              {/* Decorative quote */}

              <div className="mt-10 pl-6 border-l-4 border-orange-400">

                <p className="text-white text-xl font-bold italic">
                  "Every child is a unique learner."
                </p>

              </div>

            </motion.div>


            <div className="grid sm:grid-cols-2 gap-5">

              {[
                {
                  icon: <FaLightbulb />,
                  title: "Curiosity",
                  text: "Ask, explore and discover.",
                },
                {
                  icon: <FaComments />,
                  title: "Communication",
                  text: "Express ideas with confidence.",
                },
                {
                  icon: <FaHeart />,
                  title: "Confidence",
                  text: "Build positive relationships.",
                },
                {
                  icon: <FaSeedling />,
                  title: "Growth",
                  text: "Nurture unique potential.",
                },
              ].map((item, index) => (

                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="bg-white/10 backdrop-blur-md border border-white/10 rounded-[30px] p-7"
                >

                  <div className="w-14 h-14 rounded-2xl bg-orange-400 text-white flex items-center justify-center text-2xl mb-5">
                    {item.icon}
                  </div>

                  <h3 className="text-xl font-black text-white mb-2">
                    {item.title}
                  </h3>

                  <p className="text-white/60 leading-7">
                    {item.text}
                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </div>

      </div>



      {/* =====================================================
          LEARNING APPROACH
      ===================================================== */}

      <div className="py-28 relative">


        {/* Background decoration */}

        <div className="absolute top-20 left-[-100px] w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-60" />

        <div className="absolute bottom-20 right-[-100px] w-72 h-72 bg-yellow-100 rounded-full blur-3xl opacity-60" />


        <div className="max-w-[1500px] mx-auto px-6 relative z-10">


          <div className="text-center mb-20">

            <div className="inline-flex px-8 py-3 rounded-full bg-orange-50 border border-orange-100 mb-7">

              <p className="uppercase tracking-[6px] text-orange-500 font-black text-sm">
                Our Strategies
              </p>

            </div>

            <h2 className="text-5xl md:text-7xl font-black text-[#07294d]">
              Learning Approach
            </h2>

            <div className="w-32 h-[6px] bg-orange-400 rounded-full mx-auto mt-8" />

            <p className="max-w-2xl mx-auto text-gray-600 text-lg leading-8 mt-7">
              A balanced approach that combines academic foundations
              with creativity, confidence, collaboration and joy.
            </p>

          </div>


          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-7">

            {strategies.map((item, index) => (

              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 40,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="relative bg-white rounded-[35px] p-9 shadow-[0_25px_80px_rgba(0,0,0,0.08)] border border-orange-100 overflow-hidden group"
              >

                <div className="absolute right-[-15px] top-[-20px] text-[100px] font-black text-orange-50">
                  0{index + 1}
                </div>


                <div className="relative z-10 w-20 h-20 rounded-[25px] bg-gradient-to-br from-orange-400 to-yellow-400 text-white flex items-center justify-center text-4xl shadow-xl mb-7 group-hover:scale-110 transition duration-300">
                  {item.icon}
                </div>


                <h3 className="relative z-10 text-2xl md:text-3xl font-black text-[#07294d]">
                  {item.title}
                </h3>


                <div className="w-16 h-[5px] bg-orange-300 rounded-full mt-5 mb-5" />


                <p className="text-gray-600 leading-7">
                  {item.text}
                </p>

              </motion.div>

            ))}

          </div>

        </div>

      </div>



      {/* =====================================================
          LEARNING JOURNEY
      ===================================================== */}

      <div className="py-28 bg-orange-50">

        <div className="max-w-[1500px] mx-auto px-6">

          <div className="text-center mb-20">

            <p className="uppercase tracking-[5px] text-orange-500 font-black text-sm mb-5">
              The Journey
            </p>

            <h2 className="text-5xl md:text-7xl font-black text-[#07294d]">
              Growing Step By Step
            </h2>

            <p className="max-w-2xl mx-auto text-gray-600 text-lg mt-6 leading-8">
              Every stage builds naturally upon the previous one.
            </p>

          </div>


          {/* TIMELINE */}

          <div className="relative">

            {/* Desktop line */}

            <div className="hidden xl:block absolute top-[38px] left-[10%] right-[10%] h-[3px] bg-orange-200" />


            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-7">

              {journey.map((item, index) => (

                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    y: 40,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                  }}
                  viewport={{ once: true }}
                  className="relative"
                >

                  <div className="relative z-10 w-20 h-20 mx-auto rounded-full bg-orange-400 text-white flex items-center justify-center text-lg font-black shadow-xl border-8 border-orange-50">
                    {item.number}
                  </div>


                  <div className="mt-7 bg-white rounded-[35px] p-8 shadow-lg border border-orange-100 text-center">

                    <p className="uppercase tracking-[2px] text-orange-500 text-xs font-black mb-2">
                      {item.subtitle}
                    </p>

                    <h3 className="text-2xl font-black text-[#07294d] mb-4">
                      {item.title}
                    </h3>

                    <p className="text-gray-600 leading-7">
                      {item.text}
                    </p>

                  </div>

                </motion.div>

              ))}

            </div>

          </div>

        </div>

      </div>



      {/* =====================================================
          WHY IT MATTERS
      ===================================================== */}

      <div className="py-28">

        <div className="max-w-[1500px] mx-auto px-6">

          <div className="grid lg:grid-cols-2 gap-16 items-center">


            {/* IMAGE */}

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="relative"
            >

              <div className="absolute -bottom-7 -right-7 w-full h-full rounded-[45px] bg-orange-100" />

              <div className="relative rounded-[45px] overflow-hidden shadow-2xl">

                <img
                  src={playImg}
                  alt="Children learning together"
                  className="w-full h-[530px] object-cover hover:scale-105 transition duration-700"
                />

              </div>

            </motion.div>


            {/* CONTENT */}

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >

              <p className="uppercase tracking-[5px] text-orange-500 font-black text-sm mb-5">
                Why It Matters
              </p>

              <h2 className="text-5xl md:text-6xl font-black text-[#07294d] leading-tight">

                The Early Years

                <span className="text-orange-400">
                  {" "}Matter.
                </span>

              </h2>

              <div className="w-28 h-[6px] bg-orange-400 rounded-full my-8" />

              <p className="text-gray-600 text-lg leading-9 mb-8">
                The foundational stage is about helping children
                develop the curiosity, confidence and skills they
                need for life.
              </p>


              <div className="space-y-4">

                {[
                  "Develops curiosity and independent thinking",
                  "Strengthens communication and language skills",
                  "Builds early literacy and numeracy",
                  "Encourages creativity and imagination",
                  "Develops confidence and social skills",
                ].map((point, index) => (

                  <div
                    key={index}
                    className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-orange-50"
                  >

                    <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-500 flex items-center justify-center shrink-0">
                      <FaCheckCircle />
                    </div>

                    <p className="font-semibold text-gray-700">
                      {point}
                    </p>

                  </div>

                ))}

              </div>

            </motion.div>

          </div>

        </div>

      </div>



      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <div className="pb-28">

        <div className="max-w-[1400px] mx-auto px-6">

          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[45px] bg-[#07294d] px-8 py-16 md:px-16 md:py-20 text-center"
          >

            {/* Decorative circles */}

            <div className="absolute -top-24 -right-24 w-72 h-72 bg-orange-400/20 rounded-full" />

            <div className="absolute -bottom-28 -left-20 w-80 h-80 bg-orange-400/10 rounded-full" />


            <div className="relative z-10">

              <div className="w-20 h-20 rounded-[25px] bg-orange-400 text-white text-3xl flex items-center justify-center mx-auto mb-7">
                <FaSeedling />
              </div>

              <h2 className="text-4xl md:text-6xl font-black text-white">
                Growing Today.
              </h2>

              <h3 className="text-4xl md:text-6xl font-black text-orange-400 mt-2">
                Shaping Tomorrow.
              </h3>

              <p className="max-w-2xl mx-auto text-white/70 text-lg leading-8 mt-6">
                Creating joyful learning experiences that give every
                child a strong and confident beginning.
              </p>

            </div>

          </motion.div>

        </div>

      </div>


    </section>
  );
};

export default FoundationalStage;
